import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
} from "@material-ui/core";
import { Alert, Autocomplete } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import {
  Search,
  Delete as DiscardIcon,
  DeleteOutline,
  DeleteSweep,
  Memory,
  Visibility,
  ArrowForward as ArrowForwardIcon,
  ExpandMore as ExpandMoreIcon,
  FileCopy,
} from "@material-ui/icons";
import { Sync as ReprocessIcon } from "@material-ui/icons";
import { useAuth } from "../context/ApiKeyContext";
import { useTranslation } from "../hooks/useTranslation";
import JobHeader from "../components/JobHeader";
import JobPagination from "../components/JobPagination";
import { fetchCompanies } from "../utils/userManagement";
import PopUp from "../components/PopUp";
import ProcessamentoInputCell from "../components/ProcessamentoInputCell";
import ContestacaoInputDisplay from "../components/ContestacaoInputDisplay";
import ContestacaoOutputDisplay from "../components/ContestacaoOutputDisplay";
import FileDownload from "../components/FileDownload";
import { BoxRenderResult } from "../components/RenderHTML";
import { getApiBaseUrl } from "../config";
import { jobAllowsManualReprocess } from "../utils/jobReprocess";
import {
  persistProcessamentoJobForReplication,
  processamentoJobHasReplicateData,
} from "../utils/replicateProcessamentoJob";
import {
  persistContestacaoJobForReplication,
  contestacaoJobAllowsRecreateFromRow,
} from "../utils/replicateContestacaoJob";

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "var(--ikaro-gray-light)",
    overflow: "hidden",
    minHeight: 0, // Importante para que o flex funcione corretamente
  },
  // Header fixo no topo
  headerSection: {
    flexShrink: 0,
  },
  // Filtros (quando visíveis)
  filtersSection: {
    flexShrink: 0,
    padding: "0 16px",
  },
  filtersPaper: {
    padding: theme.spacing(2),
    backgroundColor: "var(--ikaro-text-white)",
    borderRadius: 0,
    boxShadow: "none",
    border: "1px solid var(--ikaro-gray-medium)",
    marginBottom: theme.spacing(1),
  },
  filtersRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    alignItems: "flex-start",
  },
  filterField: {
    minWidth: 180,
    flex: "1 1 180px",
  },
  filterFieldSmall: {
    minWidth: 150,
    flex: "0 1 180px",
  },
  jobIdsField: {
    minWidth: 300,
    flex: "2 1 300px",
  },
  searchButton: {
    backgroundColor: "var(--ikaro-orange-primary)",
    color: "#fff",
    textTransform: "none",
    fontWeight: 600,
    padding: theme.spacing(1, 3),
    "&:hover": {
      backgroundColor: "var(--ikaro-orange-dark, #c44327)",
    },
  },
  // Container principal da tabela - ocupa o espaço restante
  tableSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    minHeight: 0, // Importante para que o flex funcione corretamente
  },
  // Container compacto para a tabela
  containerCompact: {
    padding: 0,
    margin: 0,
    maxWidth: "none",
    width: "100%",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  paper: {
    backgroundColor: "var(--ikaro-text-white)",
    borderRadius: 0,
    boxShadow: "none",
    border: "1px solid var(--ikaro-gray-medium)",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  // Container para a tabela com scroll
  tableWrapper: {
    flex: 1,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  tableContainer: {
    flex: 1,
    overflow: "auto",
    maxHeight: "100%",
  },
  table: {
    minWidth: 800,
  },
  tableHead: {
    backgroundColor: "var(--ikaro-gray-light)",
    "& th": {
      fontWeight: 600,
      color: "var(--ikaro-text-dark)",
      borderBottom: "2px solid var(--ikaro-gray-medium)",
      whiteSpace: "nowrap",
      position: "sticky",
      top: 0,
      zIndex: 1,
      backgroundColor: "var(--ikaro-gray-light)",
    },
  },
  tableRow: {
    "&:hover": {
      backgroundColor: "var(--ikaro-orange-light)",
    },
  },
  statusChip: {
    fontWeight: 500,
    fontSize: "0.75rem",
  },
  statusDone: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
  },
  statusWaiting: {
    backgroundColor: "#fff3e0",
    color: "#e65100",
  },
  statusRunning: {
    backgroundColor: "#e3f2fd",
    color: "#1565c0",
  },
  statusError: {
    backgroundColor: "#ffebee",
    color: "#c62828",
  },
  statusPending: {
    backgroundColor: "#f3e5f5",
    color: "#7b1fa2",
  },
  statusDiscarded: {
    backgroundColor: "#eceff1",
    color: "#546e7a",
  },
  tasksFlowModalContent: {
    padding: theme.spacing(2, 0),
    display: "flex",
    flexWrap: "nowrap",
    alignItems: "center",
    gap: theme.spacing(1),
    overflowX: "auto",
    minHeight: 52,
  },
  taskPill: {
    padding: theme.spacing(1, 1.5),
    borderRadius: 20,
    fontWeight: 600,
    fontSize: "0.8rem",
    display: "inline-flex",
    alignItems: "center",
    flexDirection: "column",
    gap: 2,
    flexShrink: 0,
  },
  taskPillDone: { backgroundColor: "#e8f5e9", color: "#2e7d32" },
  taskPillRunning: { backgroundColor: "#e3f2fd", color: "#1565c0" },
  taskPillWaiting: { backgroundColor: "#fff8e1", color: "#f9a825" },
  taskPillError: { backgroundColor: "#ffebee", color: "#c62828" },
  taskPillGray: { backgroundColor: "#eceff1", color: "#546e7a" },
  taskFlowArrow: {
    color: "#9e9e9e",
    fontSize: 20,
    flexShrink: 0,
  },
  actionButton: {
    marginRight: theme.spacing(0.5),
  },
  reprocessButton: {
    color: "var(--ikaro-orange-primary)",
  },
  selectionToolbar: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(3),
    padding: theme.spacing(2.5, 3),
    borderBottom: "1px solid var(--ikaro-gray-medium)",
    backgroundColor: "var(--ikaro-gray-light)",
  },
  selectionToolbarPrimary: {
    minWidth: 220,
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(2.5),
  },
  selectionToolbarSecondary: {
    minWidth: 160,
  },
  discardButton: {
    color: "#f44336",
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
  },
  // Paginação fixa na base
  paginationSection: {
    flexShrink: 0,
  },
  viewModeSwitch: {
    marginLeft: theme.spacing(2),
  },
  cellEllipsis: {
    maxWidth: 200,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cellSmall: {
    maxWidth: 100,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: "var(--ikaro-gray-dark)",
  },
  buttonsRow: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    marginTop: theme.spacing(2),
  },
  // Inferências popup styles
  inferencesModalClearAllRow: {
    marginBottom: theme.spacing(1.5),
  },
  inferencesClearAllButton: {
    width: "100%",
    textTransform: "none",
    fontWeight: 600,
    backgroundColor: "var(--ikaro-status-error) !important",
    color: "var(--ikaro-text-white) !important",
    "&:hover": {
      backgroundColor: "#d32f2f !important",
    },
    "&:disabled": {
      backgroundColor: "var(--ikaro-gray-medium) !important",
      color: "var(--ikaro-text-secondary) !important",
    },
  },
  inferencesHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  inferencesCost: {
    fontWeight: "bold",
    color: "var(--ikaro-text-primary)",
  },
  inferencesContainer: {
    display: "flex",
    height: "60vh",
    minHeight: 300,
  },
  inferencesList: {
    overflowY: "auto",
    borderRight: "1px solid var(--ikaro-border-default)",
    backgroundColor: "var(--ikaro-inference-modal-list-bg)",
  },
  inferencesListItem: {
    padding: theme.spacing(1),
    cursor: "pointer",
    borderBottom: "1px solid var(--ikaro-border-default)",
    "&:hover": {
      backgroundColor: "var(--ikaro-bg-surface-hover)",
    },
  },
  inferencesListItemActive: {
    backgroundColor: "var(--ikaro-purple-light)",
  },
  splitter: {
    width: 6,
    cursor: "col-resize",
    backgroundColor: "var(--ikaro-border-default)",
  },
  inferencesDetail: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    backgroundColor: "var(--ikaro-inference-modal-detail-bg)",
  },
  inferencesDetailClearBar: {
    flexShrink: 0,
    padding: theme.spacing(1.5, 2),
    borderBottom: "1px solid var(--ikaro-border-default)",
    backgroundColor: "var(--ikaro-inference-modal-detail-toolbar-bg)",
  },
  inferencesDetailClearThisButton: {
    textTransform: "none",
    fontWeight: 600,
  },
  inferencesDetailScroll: {
    flex: 1,
    overflow: "auto",
    padding: theme.spacing(2),
    minHeight: 0,
  },
  inferenceExpansionPanel: {
    marginBottom: theme.spacing(1.5),
    boxShadow: "none",
    border: "1px solid var(--ikaro-border-default)",
    borderRadius: 4,
    backgroundColor: "var(--ikaro-bg-elevated)",
    "&:before": {
      display: "none",
    },
    "&.Mui-expanded": {
      margin: theme.spacing(0, 0, 1.5, 0),
    },
  },
  inferenceExpansionSummary: {
    minHeight: 44,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    "& .MuiExpansionPanelSummary-content": {
      margin: theme.spacing(1.25, 0),
      justifyContent: "flex-start",
    },
  },
  inferenceExpansionDetails: {
    display: "block",
    textAlign: "left",
    padding: theme.spacing(0, 2, 2),
    borderTop: "1px solid var(--ikaro-border-default)",
    "& .MuiTypography-root": {
      textAlign: "left",
    },
  },
  inferenceExpansionTitle: {
    fontWeight: 600,
    fontSize: "0.875rem",
    color: "var(--ikaro-text-primary)",
  },
  inferenceExpansionInner: {
    width: "100%",
    textAlign: "left",
  },
  panel: {
    border: "1px solid var(--ikaro-border-default)",
    borderRadius: 4,
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: "var(--ikaro-bg-elevated)",
    textAlign: "left",
  },
  panelTitle: {
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
    color: "var(--ikaro-text-secondary)",
  },
  monospaced: {
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    textAlign: "left",
  },
  inferencesButton: {
    color: "var(--ikaro-purple-accent)",
  },
  resultLink: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    cursor: "pointer",
    color: "var(--ikaro-orange-primary)",
    fontSize: "0.75rem",
    fontWeight: 500,
    "&:hover": {
      textDecoration: "underline",
      color: "var(--ikaro-orange-hover)",
    },
  },
  resultIcon: {
    fontSize: "1rem",
    color: "var(--ikaro-orange-primary)",
  },
  inputOutputCell: {
    minWidth: 150,
  },
}));

// Lista de status no filtro (enum JobStatus; PENDING_APPROVAL só aparece mascarado na listagem)
const STATUS_OPTIONS = [
  "WAITING",
  "RUNNING",
  "DONE",
  "ERROR",
  "FINAL_ERROR",
  "ERROR_FINALIZING",
  "PENDING_APPROVAL",
  "APPROVED",
  "FINALIZING",
  "DISCARDED",
];

// Serviços disponíveis (value enviado no filtro; backend normaliza para product code)
const SERVICES = [
  { value: "DOCUMENT_CREATION", label: "Criador de Documentos" },
  { value: "PROCESS_DIGITAL_PDF", label: "Processamento de PDF" },
];

// job.service pode vir como product code (DC_CONTESTACAO, PE_CUSTOM) ou legado
const isDocumentCreationService = (service) =>
  service === "DOCUMENT_CREATION" ||
  service === "DC_CONTESTACAO" ||
  service === "DC_CONTESTACAO_DOSSIE" ||
  (typeof service === "string" && service.includes("DC_CONTESTACAO"));
const isProcessPdfService = (service) =>
  service === "PROCESS_DIGITAL_PDF" ||
  service === "PE_CUSTOM" ||
  (typeof service === "string" && service.includes("PE_CUSTOM"));

/** Nome amigável do serviço Ikaro a partir do código de produto (jobs.code). */
function getServiceLabelFromProductCode(productCode) {
  if (!productCode) return "—";
  const pc = String(productCode).split(",")[0].trim();
  if (pc === "DC_CONTESTACAO_DOSSIE") {
    return "Dossiê para contestação";
  }
  if (isProcessPdfService(pc)) {
    return (
      SERVICES.find((s) => s.value === "PROCESS_DIGITAL_PDF")?.label || pc
    );
  }
  if (
    isDocumentCreationService(pc) ||
    pc.startsWith("DC_") ||
    (typeof pc === "string" && pc.includes("CONTEST"))
  ) {
    return (
      SERVICES.find((s) => s.value === "DOCUMENT_CREATION")?.label || pc
    );
  }
  return pc;
}

/** Rótulo traduzido do produto (coluna Serviço) a partir de jobs.code. */
function getProductDisplayLabel(serviceCode, tr) {
  if (!serviceCode) return "—";
  const pc = String(serviceCode).split(",")[0].trim();
  const key = `jobStatusViewer.productCodes.${pc}`;
  const translated = tr(key);
  if (translated && translated !== key) return translated;
  return getServiceLabelFromProductCode(serviceCode);
}

/** Serviço Ikaro (ex.: DOCUMENT_CREATION) para checar permissão global_discard_job. */
function getServiceNameForDiscardPermission(serviceCode) {
  if (!serviceCode) return null;
  const pc = String(serviceCode).split(",")[0].trim();
  if (isProcessPdfService(pc)) return "PROCESS_DIGITAL_PDF";
  if (
    isDocumentCreationService(pc) ||
    pc.startsWith("DC_") ||
    (typeof pc === "string" && pc.includes("CONTEST"))
  ) {
    return "DOCUMENT_CREATION";
  }
  return pc;
}

/** Uma linha da tabela a partir do payload de `search_jobs_global`. */
function mapGlobalSearchJobToRow(job) {
  return {
    job_id: job.job_id,
    code: job.servico,
    insertion_timestamp: job.data_insercao,
    company_id: job.empresa_id,
    company_name: job.empresa_nome,
    service: job.servico,
    product_code: job.servico,
    service_name: getServiceLabelFromProductCode(job.servico),
    status: job.status,
    error_handled: job.erro_tratado,
    error_description: job.erro_descricao,
    tasks_flow: job.tasks_flow,
    tasks: job.tasks,
    cell_id: job.celula_id,
    cell_name: job.nome_celula,
    job_name: job.job_name,
    api_key_id: job.api_key_id,
    api_key_name: job.api_key_nome,
    user_id: job.usuario_id,
    user_name: job.usuario_nome,
    input_params: job.input_params,
    input_payload: job.input_payload,
    output_params: job.output_params,
    output_payload: job.output_payload,
  };
}

/**
 * Texto da coluna de fluxo: tarefa em RUNNING, senão próxima WAITING, senão erro de task, senão resumo.
 * @param {function} tr — t do namespace jobs
 */
function getCurrentTaskFocusLabel(job, tr) {
  const tasks = (job.tasks || [])
    .slice()
    .sort(
      (a, b) => (a.sequence_number ?? 0) - (b.sequence_number ?? 0),
    );
  if (!tasks.length) {
    return job.tasks_flow || "—";
  }
  const typeLabel = (task) =>
    tr(`jobStatusViewer.taskTypes.${task.task_type}`) ||
    task.task_type ||
    "—";
  const running = tasks.find((x) => x.status === "RUNNING");
  if (running) {
    return typeLabel(running);
  }
  const waiting = tasks.find((x) => x.status === "WAITING");
  if (waiting) {
    return `${typeLabel(waiting)} (${tr("jobStatusViewer.status.WAITING")})`;
  }
  const failed = tasks.find(
    (x) => x.status === "ERROR" || x.status === "FINAL_ERROR",
  );
  if (failed) {
    const st = tr(`jobStatusViewer.status.${failed.status}`) || failed.status;
    return `${typeLabel(failed)} (${st})`;
  }
  const allTerminal = tasks.every(
    (x) => x.status === "DONE" || x.status === "DISCARDED",
  );
  if (allTerminal) {
    return "—";
  }
  return job.tasks_flow || "—";
}

const JobStatusViewerPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const { token, hasRole } = useAuth();
  const { t, formatDateTime } = useTranslation("jobs");
  const { t: tCommon } = useTranslation("common");

  // Verificar permissões
  const isAdmin = hasRole("ADMIN", "admin");
  const hasDocCreationPermission = hasRole(
    "DOCUMENT_CREATION",
    "global_view_jobs_status",
  );
  const hasProcessPdfPermission = hasRole(
    "PROCESS_DIGITAL_PDF",
    "global_view_jobs_status",
  );
  const canReprocess = hasRole("ADMIN", "re_execute_job");
  const canViewInferences = hasRole("ADMIN", "inferences_viewer");
  /* Na visão global de status, permitir recriar com o mesmo critério de acesso à tela
   * (global_view_jobs_status), não só exec_process — vale para qualquer status do job. */
  const canRecreateProcessPdf =
    hasRole("PROCESS_DIGITAL_PDF", "exec_process") ||
    hasRole("PROCESS_DIGITAL_PDF", "global_view_jobs_status");
  const canRecreateContestacao =
    hasRole("DOCUMENT_CREATION", "exec_process") ||
    hasRole("DOCUMENT_CREATION", "global_view_jobs_status");

  const handleRecreateFromTask = (job) => {
    if (
      canRecreateProcessPdf &&
      isProcessPdfService(job.service) &&
      processamentoJobHasReplicateData(job)
    ) {
      const shim = {
        job_name: job.job_name,
        input_payload: job.input_payload,
        input_params: job.input_params,
      };
      if (persistProcessamentoJobForReplication(shim)) {
        history.push("/processamento/novo");
      }
      return;
    }
    if (canRecreateContestacao && contestacaoJobAllowsRecreateFromRow(job)) {
      if (persistContestacaoJobForReplication(job)) {
        history.push("/contestacao/nova");
      }
    }
  };

  const showRecreateFromTaskButton = (job) =>
    (canRecreateProcessPdf &&
      isProcessPdfService(job.service) &&
      processamentoJobHasReplicateData(job)) ||
    (canRecreateContestacao && contestacaoJobAllowsRecreateFromRow(job));

  const canDiscardJob = (job) => {
    if (isAdmin) return true;
    const svc = getServiceNameForDiscardPermission(job.service);
    if (!svc) return false;
    return hasRole(svc, "global_discard_job");
  };

  // Determinar serviços disponíveis para o usuário
  const getAvailableServices = () => {
    if (isAdmin) {
      return SERVICES;
    }
    const available = [];
    if (hasDocCreationPermission) {
      available.push(SERVICES.find((s) => s.value === "DOCUMENT_CREATION"));
    }
    if (hasProcessPdfPermission) {
      available.push(SERVICES.find((s) => s.value === "PROCESS_DIGITAL_PDF"));
    }
    return available.filter(Boolean);
  };

  const availableServices = getAvailableServices();

  // Estados dos filtros
  const getDefaultStartDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split("T")[0];
  };

  const [filters, setFilters] = useState({
    dateStart: getDefaultStartDate(),
    dateEnd: "",
    company: null,
    service: "",
    productCode: "",
    status: [], // vazio = todos os status (inclui em andamento: WAITING/RUNNING)
    jobName: "",
    jobIds: "",
  });

  // Estados da tabela
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [isCompleteView, setIsCompleteView] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  // Estados para companies
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  // Estado para modal de confirmação
  const [discardDialog, setDiscardDialog] = useState({
    open: false,
    job: null,
  });
  const [reprocessDialog, setReprocessDialog] = useState({
    open: false,
    job: null,
    jobIds: null,
  });
  const [selectedReprocessIds, setSelectedReprocessIds] = useState([]);

  // Estado para modal de erro
  const [errorDialog, setErrorDialog] = useState({
    open: false,
    description: "",
  });

  // Estado para modal do fluxo de tarefas (visual com pills e setas)
  const [tasksFlowModal, setTasksFlowModal] = useState({
    open: false,
    job: null,
  });
  const [tasksFlowBilling, setTasksFlowBilling] = useState([]);

  // Estados para inferências
  const [inferencesOpen, setInferencesOpen] = useState(false);
  const [inferencesLoading, setInferencesLoading] = useState(false);
  const [inferencesError, setInferencesError] = useState(null);
  const [inferencesData, setInferencesData] = useState({
    total_cost: 0,
    inferences: [],
  });
  const [selectedInferenceHash, setSelectedInferenceHash] = useState(null);
  const [inferenceDetailsLoading, setInferenceDetailsLoading] = useState(false);
  const [inferenceDetails, setInferenceDetails] = useState(null);
  const [leftPaneWidth, setLeftPaneWidth] = useState(150);
  const [inferencesJob, setInferencesJob] = useState(null);
  const [invalidateSingleCacheLoading, setInvalidateSingleCacheLoading] =
    useState(false);
  const [invalidateAllCacheLoading, setInvalidateAllCacheLoading] =
    useState(false);

  // Estado para popup de resultado
  const [resultPopupOpen, setResultPopupOpen] = useState(false);
  const [resultPopupContent, setResultPopupContent] = useState(null);

  // URL base da API
  const API_BASE_URL = getApiBaseUrl();

  // Carregar companies
  useEffect(() => {
    const loadCompanies = async () => {
      if (!token) return;
      setLoadingCompanies(true);
      try {
        const data = await fetchCompanies(token);
        setCompanies(data);
      } catch (err) {
        console.error("Erro ao carregar empresas:", err);
      } finally {
        setLoadingCompanies(false);
      }
    };
    loadCompanies();
  }, [token]);

  // Carregar billing_consumption ao abrir o modal de tarefas
  useEffect(() => {
    if (!tasksFlowModal.open || !tasksFlowModal.job?.job_id || !token) {
      setTasksFlowBilling([]);
      return;
    }
    let cancelled = false;
    const loadBilling = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/super_admin/job/${tasksFlowModal.job.job_id}/billing_consumption`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled && data.billing_consumption) setTasksFlowBilling(data.billing_consumption);
      } catch (err) {
        if (!cancelled) setTasksFlowBilling([]);
      }
    };
    loadBilling();
    return () => { cancelled = true; };
  }, [tasksFlowModal.open, tasksFlowModal.job?.job_id, token, API_BASE_URL]);

  // Função de pesquisa (viewModeOverride: quando passado, usa no lugar de isCompleteView - evita race com setState ao alternar "Dados completos")
  const handleSearch = useCallback(
    async (viewModeOverride) => {
      setLoading(true);
      setError(null);

      try {
        // Preparar job_ids se fornecido
        let jobIdsArray = null;
        if (filters.jobIds && filters.jobIds.trim()) {
          jobIdsArray = filters.jobIds
            .split(/[\n,]/)
            .map((id) => id.trim())
            .filter(Boolean)
            .map((id) => parseInt(id, 10))
            .filter((id) => !isNaN(id));
        }

        const useCompleteView =
          viewModeOverride !== undefined ? viewModeOverride : isCompleteView;

        // Montar o corpo da requisição
        const requestBody = {
          data_inicio: filters.dateStart, // Obrigatório
          data_fim: filters.dateEnd || undefined,
          company_name: filters.company?.name || undefined,
          service_code: filters.service || undefined,
          product_code:
            filters.service === "DOCUMENT_CREATION" && filters.productCode
              ? filters.productCode
              : undefined,
          status_list: filters.status.length > 0 ? filters.status : undefined,
          job_name: filters.jobName || undefined,
          job_ids:
            jobIdsArray && jobIdsArray.length > 0 ? jobIdsArray : undefined,
          view_mode: useCompleteView ? "COMPLETO" : "SIMPLIFICADO",
          page: pagination.page,
          page_size: pagination.pageSize,
        };

        const response = await fetch(
          `${API_BASE_URL}/super_admin/search_jobs_global`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
          },
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.detail ||
              `Erro ${response.status}: ${response.statusText}`,
          );
        }

        const data = await response.json();

        const mappedJobs = (data.jobs || []).map(mapGlobalSearchJobToRow);

        setJobs(mappedJobs);
        setPagination((prev) => ({
          ...prev,
          total: data.pagination?.total_count || 0,
          totalPages: data.pagination?.total_pages || 0,
        }));
      } catch (err) {
        console.error("Erro na pesquisa:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [
      filters,
      pagination.page,
      pagination.pageSize,
      isCompleteView,
      token,
      API_BASE_URL,
    ],
  );

  /** Atualiza só as linhas dos job_ids (sem recarregar a página inteira). */
  const refreshJobsInList = useCallback(
    async (jobIds) => {
      const ids = [...new Set((jobIds || []).filter(Boolean))];
      if (!ids.length || !token || !filters.dateStart) return;
      try {
        const requestBody = {
          data_inicio: filters.dateStart,
          data_fim: filters.dateEnd || undefined,
          company_name: filters.company?.name || undefined,
          service_code: filters.service || undefined,
          product_code:
            filters.service === "DOCUMENT_CREATION" && filters.productCode
              ? filters.productCode
              : undefined,
          status_list: filters.status.length > 0 ? filters.status : undefined,
          job_name: filters.jobName || undefined,
          job_ids: ids,
          view_mode: isCompleteView ? "COMPLETO" : "SIMPLIFICADO",
          page: 1,
          page_size: ids.length,
        };
        const response = await fetch(
          `${API_BASE_URL}/super_admin/search_jobs_global`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
          },
        );
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.detail ||
              `Erro ${response.status}: ${response.statusText}`,
          );
        }
        const data = await response.json();
        const mappedJobs = (data.jobs || []).map(mapGlobalSearchJobToRow);
        setJobs((prev) => {
          const byId = new Map(mappedJobs.map((j) => [j.job_id, j]));
          return prev.map((row) => byId.get(row.job_id) ?? row);
        });
      } catch (err) {
        console.error("Erro ao atualizar linha(s):", err);
        handleSearch();
      }
    },
    [filters, isCompleteView, token, API_BASE_URL, handleSearch],
  );

  // Handlers de filtros
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleStatusChange = (event) => {
    setFilters((prev) => ({
      ...prev,
      status: event.target.value,
    }));
    // Voltar para página 1 quando filtros mudarem
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Handlers de paginação
  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: newPageSize,
      page: 1,
    }));
  };

  const handleConfirmReprocess = async () => {
    const ids =
      reprocessDialog.jobIds && reprocessDialog.jobIds.length
        ? reprocessDialog.jobIds
        : reprocessDialog.job
          ? [reprocessDialog.job.job_id]
          : [];
    if (!ids.length) return;
    setReprocessDialog({ open: false, job: null, jobIds: null });
    try {
      for (const id of ids) {
        const response = await fetch(
          `${API_BASE_URL}/job/${id}/reprocess`,
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.detail || `Erro ao reprocessar job ${id}`);
        }
      }
      setSuccessMessage(
        ids.length > 1
          ? t("jobStatusViewer.reprocessBulkSuccess", { count: ids.length })
          : t("jobStatusViewer.reprocessSuccess"),
      );
      await refreshJobsInList(ids);
      setSelectedReprocessIds([]);
    } catch (err) {
      setError(err.message);
    }
  };

  // Handler de descarte
  const handleDiscard = async (job) => {
    setDiscardDialog({ open: false, job: null });
    try {
      const response = await fetch(`${API_BASE_URL}/super_admin/discard_job`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ job_id: job.job_id }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail || `Erro ${response.status}: ${response.statusText}`,
        );
      }

      const data = await response.json();
      setSuccessMessage(data.message || t("jobStatusViewer.discardSuccess"));

      await refreshJobsInList([job.job_id]);
    } catch (err) {
      console.error("Erro ao descartar job:", err);
      setError(err.message);
    }
  };

  // Função para abrir inferências de um job
  const openInferences = async (job) => {
    if (!API_BASE_URL || !token || !job?.job_id) return;
    try {
      setInferencesError(null);
      setInferencesLoading(true);
      setInferencesOpen(true);
      setInferencesJob(job);
      setInferenceDetails(null);
      setSelectedInferenceHash(null);

      const url = `${API_BASE_URL}/job/get_job_inferences?job_id=${encodeURIComponent(
        job.job_id,
      )}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      setInferencesData({
        total_cost: data.total_cost || 0,
        inferences: Array.isArray(data.inferences) ? data.inferences : [],
      });
    } catch (err) {
      setInferencesError(err.message || "Erro ao carregar inferências");
    } finally {
      setInferencesLoading(false);
    }
  };

  // Função para buscar detalhes de uma inferência
  const fetchInferenceDetails = async (hash) => {
    if (!API_BASE_URL || !token || !hash) return;
    try {
      setInferenceDetailsLoading(true);
      setInferenceDetails(null);
      const url = `${API_BASE_URL}/job/get_inference_details?hash=${encodeURIComponent(
        hash,
      )}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      setInferenceDetails(data);
    } catch (err) {
      setInferenceDetails({});
    } finally {
      setInferenceDetailsLoading(false);
    }
  };

  // Função auxiliar para obter a primeira tag de uma inferência
  const firstTagOfInference = (inf) => {
    if (!inf) return "-";
    if (inf.input && inf.input.tag) return inf.input.tag;
    if (inf.output && inf.output.tag) return inf.output.tag;
    if (inf.embedding && inf.embedding.tag) return inf.embedding.tag;
    return "-";
  };

  const inferenceHashSha256Re = /^[a-fA-F0-9]{64}$/;
  const isValidInferenceHash = (h) =>
    Boolean(h && inferenceHashSha256Re.test(String(h).trim()));

  const handleInvalidateCacheForSelectedHash = useCallback(async () => {
    if (!API_BASE_URL || !token) return;
    if (!isValidInferenceHash(selectedInferenceHash)) {
      setError(t("inferences.selectInferenceFirst"));
      return;
    }
    setInvalidateSingleCacheLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/job/invalidate_inference_cache`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ hash: String(selectedInferenceHash).trim() }),
        },
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || t("inferences.cacheClearError"));
      }
      setSuccessMessage(t("inferences.cacheClearedSuccess"));
    } catch (err) {
      setError(err.message || t("inferences.cacheClearError"));
    } finally {
      setInvalidateSingleCacheLoading(false);
    }
  }, [
    API_BASE_URL,
    token,
    selectedInferenceHash,
    t,
    setError,
    setSuccessMessage,
  ]);

  const handleInvalidateAllCachesForJob = useCallback(async () => {
    if (!API_BASE_URL || !token || !inferencesJob?.job_id) return;
    setInvalidateAllCacheLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/job/invalidate_inference_cache`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ job_id: inferencesJob.job_id }),
        },
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || t("inferences.cacheClearError"));
      }
      setSuccessMessage(t("inferences.cacheClearedSuccess"));
    } catch (err) {
      setError(err.message || t("inferences.cacheClearError"));
    } finally {
      setInvalidateAllCacheLoading(false);
    }
  }, [API_BASE_URL, token, inferencesJob, t, setError, setSuccessMessage]);

  // Handler para arrastar o splitter do painel de inferências
  const handleMouseDownOnSplitter = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = leftPaneWidth;

    const onMouseMove = (moveEvent) => {
      const dx = moveEvent.clientX - startX;
      const newWidth = Math.max(120, Math.min(startWidth + dx, 600));
      setLeftPaneWidth(newWidth);
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  // Função para renderizar o painel direito do popup de inferências
  const renderInferenceRightPanel = () => {
    const collapsibleSection = (key, title, inner) => (
      <ExpansionPanel
        key={key}
        className={classes.inferenceExpansionPanel}
        defaultExpanded={false}
      >
        <ExpansionPanelSummary
          expandIcon={<ExpandMoreIcon />}
          className={classes.inferenceExpansionSummary}
        >
          <Typography className={classes.inferenceExpansionTitle}>
            {title}
          </Typography>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails className={classes.inferenceExpansionDetails}>
          {inner}
        </ExpansionPanelDetails>
      </ExpansionPanel>
    );

    if (inferenceDetailsLoading) {
      return <Typography variant="body2">{tCommon("loading")}...</Typography>;
    }
    if (!inferenceDetails || Object.keys(inferenceDetails).length === 0) {
      return (
        <Typography variant="body2" className={classes.monospaced}>
          {t("inferences.noResultForHash", {
            hash: selectedInferenceHash || "-",
          })}
        </Typography>
      );
    }

    if (inferenceDetails.type === "chat") {
      const inputTokens = inferenceDetails.costs?.input ?? 0;
      const outputTokens = inferenceDetails.costs?.output ?? 0;
      const systemMessage = inferenceDetails.system_message;
      const userMessage = inferenceDetails.user_message;
      const result = inferenceDetails.result;
      const pdfFile = inferenceDetails.pdf;
      const imageBase64 = inferenceDetails.image_base64;
      const imageMediaType = inferenceDetails.image_media_type;
      const costRow = (inferencesData.inferences || []).find(
        (row) => row?.hash === selectedInferenceHash,
      );
      const costUsd =
        costRow != null &&
        costRow.cost_usd != null &&
        costRow.cost_usd !== ""
          ? Number(costRow.cost_usd)
          : null;

      return (
        <Box>
          <Typography variant="h6" gutterBottom>
            {t("inferences.chatTitle")}
          </Typography>
          {collapsibleSection(
            "consumption",
            t("inferences.consumption"),
            <Box className={classes.inferenceExpansionInner}>
              <Typography variant="body2" color="textPrimary">
                {t("inferences.inputTokens")}: {inputTokens}
              </Typography>
              <Typography variant="body2" color="textPrimary">
                {t("inferences.outputTokens")}: {outputTokens}
              </Typography>
              {costUsd != null && !Number.isNaN(costUsd) && (
                <Typography variant="body2" color="textPrimary">
                  {t("inferences.individualCostUsd")}: USD{" "}
                  {costUsd.toFixed(6)}
                </Typography>
              )}
            </Box>,
          )}
          {systemMessage &&
            collapsibleSection(
              "system",
              t("inferences.systemMessage"),
              <Typography variant="body2" className={classes.monospaced}>
                {systemMessage}
              </Typography>,
            )}
          {userMessage &&
            collapsibleSection(
              "user",
              t("inferences.userMessage"),
              <Typography variant="body2" className={classes.monospaced}>
                {userMessage}
              </Typography>,
            )}
          {imageBase64 &&
            imageMediaType &&
            collapsibleSection(
              "image",
              t("inferences.image"),
              <img
                alt="inference"
                src={`data:${imageMediaType};base64,${imageBase64}`}
                style={{ maxWidth: "100%" }}
              />,
            )}
          {pdfFile &&
            pdfFile.filename &&
            collapsibleSection(
              "file",
              t("inferences.file"),
              <FileDownload file={pdfFile} showError={false} />,
            )}
          {result &&
            collapsibleSection(
              "result",
              t("inferences.result"),
              <Typography variant="body2" className={classes.monospaced}>
                {result}
              </Typography>,
            )}
        </Box>
      );
    }

    if (inferenceDetails.type === "embedding") {
      const tokens = inferenceDetails.costs?.tokens ?? 0;
      const dimension = inferenceDetails.dimension ?? "-";
      const text = inferenceDetails.text;
      const vector = Array.isArray(inferenceDetails.result)
        ? inferenceDetails.result
        : [];
      const costRowEmb = (inferencesData.inferences || []).find(
        (row) => row?.hash === selectedInferenceHash,
      );
      const costUsdEmb =
        costRowEmb != null &&
        costRowEmb.cost_usd != null &&
        costRowEmb.cost_usd !== ""
          ? Number(costRowEmb.cost_usd)
          : null;

      const prettyVector = (() => {
        const parts = [];
        const perLine = 8;
        for (let i = 0; i < vector.length; i += perLine) {
          const slice = vector
            .slice(i, i + perLine)
            .map((n) => (typeof n === "number" ? n.toFixed(9) : String(n)));
          parts.push(`  ${slice.join(", ")}`);
        }
        return `[\n${parts.join("\n")}\n]`;
      })();

      return (
        <Box>
          <Typography variant="h6" gutterBottom>
            {t("inferences.embeddingsTitle")}
          </Typography>
          {collapsibleSection(
            "consumption",
            t("inferences.consumption"),
            <Box className={classes.inferenceExpansionInner}>
              <Typography variant="body2" color="textPrimary">
                {t("inferences.tokens")}: {tokens}
              </Typography>
              {costUsdEmb != null && !Number.isNaN(costUsdEmb) && (
                <Typography variant="body2" color="textPrimary">
                  {t("inferences.individualCostUsd")}: USD{" "}
                  {costUsdEmb.toFixed(6)}
                </Typography>
              )}
            </Box>,
          )}
          {collapsibleSection(
            "dimension",
            t("inferences.dimensionTitle"),
            <Typography variant="body2" color="textPrimary">
              {t("inferences.dimension")}: {dimension}
            </Typography>,
          )}
          {text &&
            collapsibleSection(
              "text",
              t("inferences.text"),
              <Typography variant="body2" className={classes.monospaced}>
                {text}
              </Typography>,
            )}
          {collapsibleSection(
            "vector",
            t("inferences.result"),
            <Typography variant="body2" className={classes.monospaced}>
              {prettyVector}
            </Typography>,
          )}
        </Box>
      );
    }

    return null;
  };

  // Função para visualizar resultado (output_payload)
  const handleViewResult = (job) => {
    if (!job.output_payload) {
      setResultPopupContent(
        <Typography>
          {tCommon("noResultsAvailable") || "Nenhum resultado disponível"}
        </Typography>,
      );
    } else {
      setResultPopupContent(
        <BoxRenderResult
          result={job.output_payload}
          rawResult={job.output_params?.raw_result}
          templateData={job.input_payload}
        />,
      );
    }
    setResultPopupOpen(true);
  };

  // Função para obter classe de status
  const getStatusClass = (status) => {
    switch (status) {
      case "DONE":
        return classes.statusDone;
      case "WAITING":
        return classes.statusWaiting;
      case "RUNNING":
      case "FINALIZING":
        return classes.statusRunning;
      case "ERROR":
      case "FINAL_ERROR":
      case "ERROR_FINALIZING":
        return classes.statusError;
      case "PENDING_APPROVAL":
      case "APPROVED":
        return classes.statusPending;
      case "DISCARDED":
        return classes.statusDiscarded;
      default:
        return "";
    }
  };

  const reprocessableIdsOnPage = jobs
    .filter((j) => jobAllowsManualReprocess(j))
    .map((j) => j.job_id);
  const allReprocessableSelected =
    reprocessableIdsOnPage.length > 0 &&
    reprocessableIdsOnPage.every((id) =>
      selectedReprocessIds.includes(id),
    );

  const toggleReprocessSelection = (jobId) => {
    setSelectedReprocessIds((prev) =>
      prev.includes(jobId)
        ? prev.filter((x) => x !== jobId)
        : [...prev, jobId],
    );
  };

  const handleSelectAllReprocessable = () => {
    if (allReprocessableSelected) {
      setSelectedReprocessIds((prev) =>
        prev.filter((id) => !reprocessableIdsOnPage.includes(id)),
      );
    } else {
      setSelectedReprocessIds((prev) => {
        const s = new Set([...prev, ...reprocessableIdsOnPage]);
        return Array.from(s);
      });
    }
  };

  const selectColumnDef = canReprocess
    ? [{ key: "select", label: "" }]
    : [];

  // Colunas da tabela (sem ordenação, pois a API não suporta)
  const completeColumns = [
    ...selectColumnDef,
    { key: "job_id", label: t("jobStatusViewer.columns.jobId") },
    {
      key: "insertion_timestamp",
      label: t("jobStatusViewer.columns.insertionDate"),
    },
    {
      key: "company_name",
      label: t("jobStatusViewer.columns.companyName"),
    },
    {
      key: "cell_name",
      label: t("jobStatusViewer.columns.cellName"),
    },
    {
      key: "job_name",
      label: t("jobStatusViewer.columns.jobName"),
    },
    {
      key: "api_key_name",
      label: t("jobStatusViewer.columns.apiKeyName"),
    },
    {
      key: "user_name",
      label: t("jobStatusViewer.columns.userName"),
    },
    {
      key: "product_code",
      label: t("jobStatusViewer.columns.product"),
    },
    {
      key: "service_name",
      label: t("jobStatusViewer.columns.service"),
    },
    { key: "tasks_flow", label: t("jobStatusViewer.columns.tasksFlow") },
    { key: "status", label: t("jobStatusViewer.columns.status") },
    {
      key: "error_handled",
      label: t("jobStatusViewer.columns.errorHandled"),
    },
    {
      key: "error_description",
      label: t("jobStatusViewer.columns.errorDescription"),
    },
    {
      key: "input",
      label: t("jobStatusViewer.columns.input") || "INPUT",
    },
    {
      key: "output",
      label: t("jobStatusViewer.columns.output") || "OUTPUT",
    },
    { key: "actions", label: t("table.actions") },
  ];

  const simpleColumns = [
    ...selectColumnDef,
    { key: "job_id", label: t("jobStatusViewer.columns.jobId") },
    {
      key: "insertion_timestamp",
      label: t("jobStatusViewer.columns.insertionDate"),
    },
    {
      key: "company_name",
      label: t("jobStatusViewer.columns.companyName"),
    },
    {
      key: "product_code",
      label: t("jobStatusViewer.columns.product"),
    },
    {
      key: "service_name",
      label: t("jobStatusViewer.columns.service"),
    },
    { key: "tasks_flow", label: t("jobStatusViewer.columns.tasksFlow") },
    { key: "status", label: t("jobStatusViewer.columns.status") },
    {
      key: "error_handled",
      label: t("jobStatusViewer.columns.errorHandled"),
    },
    {
      key: "error_description",
      label: t("jobStatusViewer.columns.errorDescription"),
    },
    { key: "actions", label: t("table.actions") },
  ];

  const columns = isCompleteView ? completeColumns : simpleColumns;

  // Estado para controlar se a pesquisa inicial já foi feita
  const [initialSearchDone, setInitialSearchDone] = useState(false);

  // Executar pesquisa apenas na abertura da página (primeira montagem)
  useEffect(() => {
    if (!initialSearchDone && filters.dateStart) {
      handleSearch();
      setInitialSearchDone(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Executar pesquisa quando a paginação mudar (após a pesquisa inicial)
  useEffect(() => {
    if (initialSearchDone && filters.dateStart) {
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.pageSize]);

  useEffect(() => {
    setSelectedReprocessIds([]);
  }, [pagination.page, pagination.pageSize]);

  // Renderizar valor da célula
  const renderCellValue = (job, column) => {
    const value = job[column.key];

    switch (column.key) {
      case "select":
        if (!jobAllowsManualReprocess(job)) {
          return (
            <span style={{ color: "var(--ikaro-gray-medium)" }}>—</span>
          );
        }
        return (
          <Checkbox
            size="small"
            color="primary"
            checked={selectedReprocessIds.includes(job.job_id)}
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggleReprocessSelection(job.job_id)}
          />
        );
      case "insertion_timestamp":
        return formatDateTime(value, { emptyValue: "-" });
      case "status":
        return (
          <Chip
            label={t(`jobStatusViewer.status.${value}`) || value}
            size="small"
            className={`${classes.statusChip} ${getStatusClass(value)}`}
          />
        );
      case "company_name":
        return (
          <Tooltip title={`ID: ${job.company_id || "-"}`}>
            <span className={classes.cellEllipsis}>
              {value ? `${value} (${job.company_id})` : "-"}
            </span>
          </Tooltip>
        );
      case "cell_name":
        return (
          <Tooltip title={`ID: ${job.cell_id || "-"}`}>
            <span className={classes.cellEllipsis}>
              {value ? `${value} (${job.cell_id})` : "-"}
            </span>
          </Tooltip>
        );
      case "api_key_name":
        return (
          <Tooltip title={`ID: ${job.api_key_id || "-"}`}>
            <span className={classes.cellEllipsis}>
              {value ? `${value} (${job.api_key_id})` : "-"}
            </span>
          </Tooltip>
        );
      case "user_name":
        return (
          <Tooltip title={`ID: ${job.user_id || "-"}`}>
            <span className={classes.cellEllipsis}>
              {value ? `${value} (${job.user_id})` : "-"}
            </span>
          </Tooltip>
        );
      case "error_handled":
        return (
          <span
            className={classes.cellEllipsis}
            style={{ fontSize: "0.75rem" }}
          >
            {value || "-"}
          </span>
        );
      case "product_code":
        return (
          <span className={classes.cellEllipsis} title={value || ""}>
            {value || "—"}
          </span>
        );
      case "service_name":
        return (
          <span
            className={classes.cellEllipsis}
            title={getProductDisplayLabel(job.service, t) || ""}
          >
            {getProductDisplayLabel(job.service, t)}
          </span>
        );
      case "tasks_flow": {
        const viewTasksLabel = t("jobStatusViewer.tasksFlowColumn.viewTasks");
        const hasTasksData =
          Boolean(job.tasks_flow) ||
          Boolean(job.tasks && job.tasks.length > 0);
        const focusLabel = hasTasksData
          ? getCurrentTaskFocusLabel(job, t)
          : "—";
        const displayLabel =
          !hasTasksData || focusLabel === "—"
            ? viewTasksLabel
            : focusLabel;
        const flowSummary = job.tasks_flow || "";
        const openHint = t("jobStatusViewer.tasksFlowColumn.openDetails");
        const tooltipTitle = flowSummary
          ? `${flowSummary}\n${openHint}`
          : openHint;
        return (
          <Tooltip title={tooltipTitle}>
            <span className="job-status-tasks-flow-cell-wrap">
              <button
                type="button"
                className="ikaro-btn ikaro-btn-sm ikaro-btn-secondary job-status-tasks-flow-btn"
                onClick={() => setTasksFlowModal({ open: true, job })}
              >
                {displayLabel}
              </button>
            </span>
          </Tooltip>
        );
      }
      case "error_description":
        if (!value) return "-";
        const shortDescription =
          value.length > 40 ? `${value.substring(0, 40)}...` : value;
        return (
          <Tooltip title="Clique para ver descrição completa">
            <span
              className={classes.cellEllipsis}
              style={{
                cursor: "pointer",
                color: "var(--ikaro-orange-primary)",
              }}
              onClick={() => setErrorDialog({ open: true, description: value })}
            >
              {shortDescription}
            </span>
          </Tooltip>
        );
      case "input":
        // Renderização dinâmica baseada no service (aceita product code ou legado)
        if (isDocumentCreationService(job.service)) {
          return (
            <Box className={classes.inputOutputCell}>
              <ContestacaoInputDisplay inputPayload={job.input_payload} />
            </Box>
          );
        } else if (isProcessPdfService(job.service)) {
          return (
            <Box className={classes.inputOutputCell}>
              <ProcessamentoInputCell job={job} />
            </Box>
          );
        }
        // Fallback para outros serviços - mostrar JSON resumido
        return job.input_payload ? (
          <Tooltip title={JSON.stringify(job.input_payload, null, 2)}>
            <span className={`${classes.cellEllipsis} ${classes.cellSmall}`}>
              {typeof job.input_payload === "object"
                ? JSON.stringify(job.input_payload).substring(0, 30) + "..."
                : job.input_payload}
            </span>
          </Tooltip>
        ) : (
          "-"
        );
      case "output":
        // Renderização dinâmica baseada no service (aceita product code ou legado)
        if (isDocumentCreationService(job.service)) {
          return (
            <Box className={classes.inputOutputCell}>
              <ContestacaoOutputDisplay
                outputPayload={job.output_payload}
                status={job.status}
              />
            </Box>
          );
        } else if (isProcessPdfService(job.service)) {
          // Para processamento de PDF, usar link para visualizar resultado
          return job.output_payload ? (
            <Box className={classes.inputOutputCell}>
              <Link
                component="button"
                className={classes.resultLink}
                onClick={() => handleViewResult(job)}
              >
                <Visibility className={classes.resultIcon} />
                {t("table.result") || "Resultado"}
              </Link>
            </Box>
          ) : (
            <Typography
              variant="body2"
              className="text-muted"
            >
              {tCommon("notAvailable") || "-"}
            </Typography>
          );
        }
        // Fallback para outros serviços - mostrar JSON resumido
        return job.output_payload ? (
          <Tooltip title={JSON.stringify(job.output_payload, null, 2)}>
            <span className={`${classes.cellEllipsis} ${classes.cellSmall}`}>
              {typeof job.output_payload === "object"
                ? JSON.stringify(job.output_payload).substring(0, 30) + "..."
                : job.output_payload}
            </span>
          </Tooltip>
        ) : (
          "-"
        );
      case "actions":
        return (
          <Box display="flex">
            {/* Botão de inferências */}
            {canViewInferences && (
              <Tooltip
                title={t("inferences.viewInferences") || "Ver Inferências"}
              >
                <IconButton
                  size="small"
                  className={`${classes.actionButton} ${classes.inferencesButton}`}
                  onClick={() => openInferences(job)}
                >
                  <Memory fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {showRecreateFromTaskButton(job) && (
              <Tooltip title={t("actions.recreateFromTaskTooltip")}>
                <IconButton
                  size="small"
                  className={classes.actionButton}
                  onClick={() => handleRecreateFromTask(job)}
                >
                  <FileCopy style={{ fontSize: "1.125rem" }} />
                </IconButton>
              </Tooltip>
            )}
            {canReprocess && jobAllowsManualReprocess(job) && (
              <Tooltip title={t("jobStatusViewer.actions.reprocess")}>
                <IconButton
                  size="small"
                  className={`${classes.actionButton} ${classes.reprocessButton}`}
                  onClick={() =>
                    setReprocessDialog({
                      open: true,
                      job,
                      jobIds: null,
                    })
                  }
                >
                  <ReprocessIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {job.status === "FINAL_ERROR" && canDiscardJob(job) && (
              <Tooltip title={t("jobStatusViewer.actions.discard")}>
                <IconButton
                  size="small"
                  className={`${classes.actionButton} ${classes.discardButton}`}
                  onClick={() => setDiscardDialog({ open: true, job })}
                >
                  <DiscardIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      default:
        return (
          <span className={classes.cellEllipsis}>
            {value?.toString() || "-"}
          </span>
        );
    }
  };

  // Header actions (sem botão de refresh)
  const headerActions = [];

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.headerSection}>
        <JobHeader
          title={t("jobStatusViewer.title")}
          subtitle={t("jobStatusViewer.subtitle")}
          showFilters={showFilters}
          onToggleFilters={() => setShowFilters(!showFilters)}
          customActions={headerActions}
          showNewJobButton={false}
        />
      </Box>

      {/* Filtros */}
      {showFilters && (
        <Box className={classes.filtersSection}>
          <Paper className={classes.filtersPaper} elevation={0}>
            <Box className={classes.filtersRow}>
              {/* Data Início */}
              <TextField
                label={t("filters.dateStart")}
                type="date"
                value={filters.dateStart}
                onChange={(e) =>
                  handleFilterChange("dateStart", e.target.value)
                }
                className={classes.filterFieldSmall}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
              />

              {/* Data Fim */}
              <TextField
                label={t("filters.dateEnd")}
                type="date"
                value={filters.dateEnd}
                onChange={(e) => handleFilterChange("dateEnd", e.target.value)}
                className={classes.filterFieldSmall}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
              />

              {/* Company */}
              <Autocomplete
                options={companies}
                getOptionLabel={(option) => option.name || ""}
                value={filters.company}
                onChange={(e, newValue) =>
                  handleFilterChange("company", newValue)
                }
                loading={loadingCompanies}
                className={classes.filterField}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("filters.company")}
                    variant="outlined"
                    size="small"
                    placeholder={t("filters.companyPlaceholder")}
                  />
                )}
              />

              {/* Serviço */}
              <FormControl
                variant="outlined"
                size="small"
                className={classes.filterField}
              >
                <InputLabel>{t("jobStatusViewer.filters.service")}</InputLabel>
                <Select
                  value={filters.service}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFilters((prev) => ({
                      ...prev,
                      service: v,
                      productCode:
                        v === "DOCUMENT_CREATION" ? prev.productCode : "",
                    }));
                    setPagination((p) => ({ ...p, page: 1 }));
                  }}
                  label={t("jobStatusViewer.filters.service")}
                  displayEmpty
                >
                  <MenuItem value="">
                    {t("jobStatusViewer.filters.allServices")}
                  </MenuItem>
                  {availableServices.map((service) => (
                    <MenuItem key={service.value} value={service.value}>
                      {service.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Produto (Criador de Documentos) */}
              {filters.service === "DOCUMENT_CREATION" && (
                <FormControl
                  variant="outlined"
                  size="small"
                  className={classes.filterField}
                >
                  <InputLabel>
                    {t("jobStatusViewer.filters.documentCreationProduct")}
                  </InputLabel>
                  <Select
                    value={filters.productCode}
                    onChange={(e) => {
                      handleFilterChange("productCode", e.target.value);
                      setPagination((p) => ({ ...p, page: 1 }));
                    }}
                    label={t("jobStatusViewer.filters.documentCreationProduct")}
                  >
                    <MenuItem value="">
                      {t("jobStatusViewer.filters.allDocumentCreationProducts")}
                    </MenuItem>
                    <MenuItem value="DC_CONTESTACAO">
                      {t("jobStatusViewer.filters.productContestacao")}
                    </MenuItem>
                    <MenuItem value="DC_CONTESTACAO_DOSSIE">
                      {t("jobStatusViewer.filters.productDossieContestacao")}
                    </MenuItem>
                  </Select>
                </FormControl>
              )}

              {/* Status (multi-seleção) */}
              <FormControl
                variant="outlined"
                size="small"
                className={classes.filterField}
              >
                <InputLabel>{t("filters.status")}</InputLabel>
                <Select
                  multiple
                  value={filters.status}
                  onChange={handleStatusChange}
                  label={t("filters.status")}
                  renderValue={(selected) =>
                    selected
                      .map((s) => t(`jobStatusViewer.status.${s}`) || s)
                      .join(", ")
                  }
                >
                  {STATUS_OPTIONS.map((status) => (
                    <MenuItem key={status} value={status}>
                      <Checkbox checked={filters.status.includes(status)} />
                      <ListItemText
                        primary={
                          t(`jobStatusViewer.status.${status}`) || status
                        }
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Job Name */}
              <TextField
                label={t("jobStatusViewer.filters.jobName")}
                value={filters.jobName}
                onChange={(e) => handleFilterChange("jobName", e.target.value)}
                className={classes.filterField}
                variant="outlined"
                size="small"
                placeholder={t("jobStatusViewer.filters.jobNamePlaceholder")}
              />
            </Box>

            {/* Job IDs (multi-line) */}
            <Box mt={2}>
              <TextField
                label={t("jobStatusViewer.filters.jobIds")}
                value={filters.jobIds}
                onChange={(e) => handleFilterChange("jobIds", e.target.value)}
                className={classes.jobIdsField}
                variant="outlined"
                size="small"
                multiline
                rows={4}
                placeholder={t("jobStatusViewer.filters.jobIdsPlaceholder")}
                helperText={t("jobStatusViewer.filters.jobIdsHelper")}
                fullWidth
              />
            </Box>

            {/* Botões e switch de visualização */}
            <Box className={classes.buttonsRow}>
              <Button
                variant="contained"
                className={classes.searchButton}
                onClick={handleSearch}
                disabled={loading}
                startIcon={
                  loading ? <CircularProgress size={20} /> : <Search />
                }
              >
                {t("jobStatusViewer.search")}
              </Button>

              <FormControlLabel
                control={
                  <Switch
                    checked={isCompleteView}
                    onChange={(e) => {
                      const newVal = e.target.checked;
                      setIsCompleteView(newVal);
                      handleSearch(newVal);
                    }}
                    color="primary"
                  />
                }
                label={t("jobStatusViewer.completeView")}
                className={classes.viewModeSwitch}
              />
            </Box>
          </Paper>
        </Box>
      )}

      {/* Área principal da tabela - ocupa o espaço restante */}
      <Box className={classes.tableSection}>
        <Box className={classes.containerCompact}>
          <Paper className={classes.paper}>
            {loading ? (
              <Box className={classes.loading}>
                <CircularProgress />
                <Typography variant="body1" className="ml-16">
                  {t("table.loadingJobs")}
                </Typography>
              </Box>
            ) : (
              <>
                {canReprocess && selectedReprocessIds.length > 0 && (
                  <Box className={classes.selectionToolbar}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="medium"
                      className={`${classes.searchButton} ${classes.selectionToolbarPrimary}`}
                      startIcon={<ReprocessIcon />}
                      onClick={() =>
                        setReprocessDialog({
                          open: true,
                          job: null,
                          jobIds: [...selectedReprocessIds],
                        })
                      }
                    >
                      {t("jobStatusViewer.reprocessSelected", {
                        count: selectedReprocessIds.length,
                      })}
                    </Button>
                    <Button
                      variant="outlined"
                      color="default"
                      size="medium"
                      className={classes.selectionToolbarSecondary}
                      onClick={() => setSelectedReprocessIds([])}
                    >
                      {t("jobStatusViewer.clearSelection")}
                    </Button>
                  </Box>
                )}
                {/* Tabela com scroll */}
                <Box className={classes.tableWrapper}>
                  <TableContainer className={classes.tableContainer}>
                    <Table className={classes.table} stickyHeader>
                      <TableHead className={classes.tableHead}>
                        <TableRow>
                          {columns.map((column) => (
                            <TableCell key={column.key}>
                              {column.key === "select" && canReprocess ? (
                                <Checkbox
                                  size="small"
                                  color="primary"
                                  indeterminate={
                                    reprocessableIdsOnPage.some((id) =>
                                      selectedReprocessIds.includes(id),
                                    ) && !allReprocessableSelected
                                  }
                                  checked={allReprocessableSelected}
                                  onChange={handleSelectAllReprocessable}
                                />
                              ) : (
                                column.label
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {jobs.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={columns.length}
                              className={classes.emptyState}
                            >
                              <Typography variant="body1">
                                {t("table.emptyState")}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                {t("jobStatusViewer.emptyStateDescription")}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          jobs.map((job) => (
                            <TableRow
                              key={job.job_id}
                              className={classes.tableRow}
                            >
                              {columns.map((column) => (
                                <TableCell key={column.key}>
                                  {renderCellValue(job, column)}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                {/* Paginação fixa na base */}
                <Box className={classes.paginationSection}>
                  <JobPagination
                    page={pagination.page}
                    pageSize={pagination.pageSize}
                    total={pagination.total}
                    totalPages={pagination.totalPages}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                  />
                </Box>
              </>
            )}
          </Paper>
        </Box>
      </Box>

      {/* Snackbar de erro */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>

      {/* Snackbar de sucesso */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Dialog de confirmação - Descartar */}
      <Dialog
        open={discardDialog.open}
        onClose={() => setDiscardDialog({ open: false, job: null })}
      >
        <DialogTitle>{t("jobStatusViewer.confirmDiscard.title")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("jobStatusViewer.confirmDiscard.message", {
              jobId: discardDialog.job?.job_id,
            })}
          </Typography>
          <Alert severity="warning" className="mt-16">
            {t("jobStatusViewer.confirmDiscard.disclaimer")}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDiscardDialog({ open: false, job: null })}
            color="default"
          >
            {tCommon("cancel")}
          </Button>
          <Button
            onClick={() => handleDiscard(discardDialog.job)}
            color="secondary"
            variant="contained"
          >
            {t("jobStatusViewer.actions.discard")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmação - Reprocessar */}
      <Dialog
        open={reprocessDialog.open}
        onClose={() =>
          setReprocessDialog({ open: false, job: null, jobIds: null })
        }
      >
        <DialogTitle>
          {reprocessDialog.jobIds && reprocessDialog.jobIds.length > 1
            ? t("jobStatusViewer.confirmReprocess.bulkTitle")
            : t("jobStatusViewer.confirmReprocess.title")}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {reprocessDialog.jobIds && reprocessDialog.jobIds.length > 1
              ? t("jobStatusViewer.confirmReprocess.bulkMessage", {
                  count: reprocessDialog.jobIds.length,
                })
              : t("jobStatusViewer.confirmReprocess.message", {
                  jobId: reprocessDialog.job?.job_id,
                })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setReprocessDialog({ open: false, job: null, jobIds: null })
            }
            color="default"
          >
            {tCommon("cancel")}
          </Button>
          <Button
            onClick={() => handleConfirmReprocess()}
            color="primary"
            variant="contained"
          >
            {t("jobStatusViewer.actions.reprocess")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de visualização de erro */}
      <Dialog
        open={errorDialog.open}
        onClose={() => setErrorDialog({ open: false, description: "" })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t("jobStatusViewer.errorDialog.title")}</DialogTitle>
        <DialogContent>
          <Typography
            style={{
              whiteSpace: "pre-wrap",
              wordWrap: "break-word",
              fontFamily: "monospace",
              fontSize: "0.875rem",
              padding: "16px",
              backgroundColor: "var(--ikaro-bg-surface-secondary)",
              borderRadius: "4px",
              maxHeight: "60vh",
              overflow: "auto",
            }}
          >
            {errorDialog.description}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setErrorDialog({ open: false, description: "" })}
            color="primary"
          >
            {tCommon("close") || "Fechar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal do fluxo de tarefas (pills + setas) */}
      <Dialog
        open={tasksFlowModal.open}
        onClose={() => setTasksFlowModal({ open: false, job: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("jobStatusViewer.tasksFlowModal.title")} —{" "}
          {tasksFlowModal.job
            ? t("jobStatusViewer.tasksFlowModal.jobId", {
                id: tasksFlowModal.job.job_id,
              })
            : ""}
        </DialogTitle>
        <DialogContent>
          {tasksFlowModal.job && tasksFlowModal.job.tasks && tasksFlowModal.job.tasks.length > 0 ? (
            <Box className={classes.tasksFlowModalContent}>
              {[...tasksFlowModal.job.tasks]
                .sort((a, b) => (a.sequence_number || 0) - (b.sequence_number || 0))
                .map((task, idx) => {
                  const status = task.status || "WAITING";
                  const pillClass =
                    status === "DONE"
                      ? classes.taskPillDone
                      : status === "RUNNING"
                      ? classes.taskPillRunning
                      : status === "WAITING"
                      ? classes.taskPillWaiting
                      : status === "ERROR" || status === "FINAL_ERROR"
                      ? classes.taskPillError
                      : classes.taskPillGray;
                  const typeLabel =
                    t(`jobStatusViewer.taskTypes.${task.task_type}`) || task.task_type || "—";
                  const statusLabel = t(`jobStatusViewer.status.${status}`) || status;
                  const taskBilling = (tasksFlowBilling || []).filter(
                    (b) => b.task_id === task.id
                  );
                  const tooltipTitle =
                    taskBilling.length > 0
                      ? taskBilling
                          .map(
                            (b) =>
                              `${b.consumption_type_name || b.consumption_unit}: ${Number(b.quantity)}`
                          )
                          .join("\n")
                      : "Sem custo registrado";
                  return (
                    <React.Fragment key={task.id != null ? task.id : idx}>
                      {idx > 0 && (
                        <ArrowForwardIcon className={classes.taskFlowArrow} />
                      )}
                      <Tooltip
                        title={<span style={{ whiteSpace: "pre-line" }}>{tooltipTitle}</span>}
                        placement="top"
                      >
                        <Box className={`${classes.taskPill} ${pillClass}`}>
                          <Typography variant="body2" style={{ fontWeight: 600 }}>
                            {typeLabel}
                          </Typography>
                          <Typography variant="caption" style={{ opacity: 0.95 }}>
                            {statusLabel}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </React.Fragment>
                  );
                })}
            </Box>
          ) : (
            <Typography color="textSecondary">
              {t("jobStatusViewer.tasksFlowModal.title")} — {tCommon("noData") || "Sem dados"}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setTasksFlowModal({ open: false, job: null })}
            color="primary"
          >
            {tCommon("close") || "Fechar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Popup de Inferências */}
      <PopUp
        open={inferencesOpen}
        onClose={() => {
          setInferencesOpen(false);
          setInferencesJob(null);
        }}
        title=""
      >
        <Box className={classes.inferencesModalClearAllRow}>
          <Button
            variant="contained"
            size="small"
            className={classes.inferencesClearAllButton}
            disabled={
              invalidateAllCacheLoading ||
              invalidateSingleCacheLoading ||
              !inferencesJob?.job_id
            }
            startIcon={
              invalidateAllCacheLoading ? (
                <CircularProgress size={18} style={{ color: "inherit" }} />
              ) : (
                <DeleteSweep fontSize="small" />
              )
            }
            onClick={handleInvalidateAllCachesForJob}
          >
            {t("inferences.clearCacheAllJob")}
          </Button>
        </Box>
        <Box className={classes.inferencesHeader}>
          <Typography variant="h6" style={{ color: "var(--ikaro-text-primary)" }}>
            {t("inferences.title") || "Inferências"}
          </Typography>
          <Typography variant="body2" className={classes.inferencesCost}>
            {t("inferences.totalCost") || "Custo Total"}: USD{" "}
            {Number(inferencesData.total_cost || 0).toFixed(6)}
          </Typography>
        </Box>
        <Box className={classes.inferencesContainer}>
          <Box
            className={classes.inferencesList}
            style={{ width: leftPaneWidth }}
          >
            {inferencesLoading ? (
              <Typography variant="body2">
                {tCommon("loading") || "Carregando"}...
              </Typography>
            ) : inferencesError ? (
              <Typography variant="body2" color="error">
                {inferencesError}
              </Typography>
            ) : (
              (inferencesData.inferences || []).map((inf, idx) => {
                const hash = inf.hash;
                const label = `${idx + 1} - ${firstTagOfInference(inf)}`;
                const active = selectedInferenceHash === hash;
                return (
                  <Box
                    key={hash || idx}
                    className={`${classes.inferencesListItem} ${
                      active ? classes.inferencesListItemActive : ""
                    }`}
                    onClick={() => {
                      setSelectedInferenceHash(hash);
                      fetchInferenceDetails(hash);
                    }}
                  >
                    <Typography
                      variant="body2"
                      style={{ fontWeight: active ? "bold" : "normal" }}
                    >
                      {label}
                    </Typography>
                  </Box>
                );
              })
            )}
          </Box>
          <Box
            className={classes.splitter}
            onMouseDown={handleMouseDownOnSplitter}
          />
          <Box className={classes.inferencesDetail}>
            <Box className={classes.inferencesDetailClearBar}>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                fullWidth
                className={classes.inferencesDetailClearThisButton}
                disabled={
                  invalidateSingleCacheLoading ||
                  invalidateAllCacheLoading ||
                  !isValidInferenceHash(selectedInferenceHash)
                }
                startIcon={
                  invalidateSingleCacheLoading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <DeleteOutline fontSize="small" />
                  )
                }
                onClick={handleInvalidateCacheForSelectedHash}
              >
                {t("inferences.clearCacheThis")}
              </Button>
            </Box>
            <Box className={classes.inferencesDetailScroll}>
              {renderInferenceRightPanel()}
            </Box>
          </Box>
        </Box>
      </PopUp>

      {/* Popup de Resultado */}
      <PopUp
        open={resultPopupOpen}
        onClose={() => setResultPopupOpen(false)}
        title=""
      >
        {resultPopupContent}
      </PopUp>
    </Box>
  );
};

export default JobStatusViewerPage;
